/**
 * Off-platform PayPal — seller stores a link; customers open PayPal in browser/app.
 */

import { Platform } from 'react-native';
import { AuthManager } from '../utils/authManager';
import {
  buildPayPalPaymentUrl,
  isPayPalDirectPaymentUrl,
  normalizePayPalDirectInput,
} from '../utils/paypalDirectPayment';

export async function getSavedPayPalDirectUrl(
  businessId: string
): Promise<string | null> {
  const owner = await AuthManager.getSavedBusinessOwner();
  if (
    owner?.id === businessId &&
    typeof owner.paypalDirectPaymentUrl === 'string' &&
    owner.paypalDirectPaymentUrl.trim()
  ) {
    return owner.paypalDirectPaymentUrl.trim();
  }
  return fetchBusinessPayPalDirectUrl(businessId);
}

export async function savePayPalDirectUrl(
  businessId: string,
  rawInput: string
): Promise<{ success: boolean; url?: string; error?: string }> {
  const normalized = normalizePayPalDirectInput(rawInput);
  if (!normalized) {
    return {
      success: false,
      error:
        'Enter a PayPal.me username (e.g. myshop) or a full PayPal payment link.',
    };
  }

  const owner = await AuthManager.getSavedBusinessOwner();
  if (!owner || owner.id !== businessId) {
    return { success: false, error: 'Business profile not found. Sign in again.' };
  }

  await AuthManager.saveBusinessOwner({
    ...owner,
    paypalDirectPaymentUrl: normalized,
  });

  return { success: true, url: normalized };
}

export async function clearPayPalDirectUrl(
  businessId: string
): Promise<{ success: boolean; error?: string }> {
  const owner = await AuthManager.getSavedBusinessOwner();
  if (!owner || owner.id !== businessId) {
    return { success: false, error: 'Business profile not found.' };
  }

  await AuthManager.saveBusinessOwner({
    ...owner,
    paypalDirectPaymentUrl: '',
  });

  return { success: true };
}

export async function fetchBusinessPayPalDirectUrl(
  businessId: string
): Promise<string | null> {
  if (!businessId) {
    return null;
  }

  try {
    if (Platform.OS === 'web') {
      const { initializeFirebase, getWebDb } = require('./unifiedAuthService');
      initializeFirebase();
      const db = getWebDb();
      if (!db) {
        return null;
      }
      const { doc, getDoc } = require('firebase/firestore');
      const snap = await getDoc(doc(db, 'businesses', businessId));
      const url = snap.data()?.paypalDirectPaymentUrl;
      return typeof url === 'string' && isPayPalDirectPaymentUrl(url) ? url : null;
    }

    const { initializeFirebase } = require('./unifiedAuthService');
    initializeFirebase();
    const { getApp } = require('@react-native-firebase/app');
    const { getFirestore, doc, getDoc } = require('@react-native-firebase/firestore');
    const db = getFirestore(getApp());
    const snap = await getDoc(doc(db, 'businesses', businessId));
    const url = snap.data()?.paypalDirectPaymentUrl;
    return typeof url === 'string' && isPayPalDirectPaymentUrl(url) ? url : null;
  } catch (e) {
    console.warn('fetchBusinessPayPalDirectUrl:', e);
    return null;
  }
}

export async function openPayPalDirectPayment(options: {
  businessId: string;
  amountUsd?: number;
  /** Pre-resolved URL (skips Firestore fetch). */
  paymentUrl?: string;
}): Promise<{ opened: boolean; error?: string }> {
  const { Linking } = require('react-native');
  const base =
    options.paymentUrl ||
    (await fetchBusinessPayPalDirectUrl(options.businessId));
  if (!base) {
    return {
      opened: false,
      error: 'This business has not set up PayPal yet.',
    };
  }

  const url = buildPayPalPaymentUrl(base, options.amountUsd);
  const canOpen = await Linking.canOpenURL(url);
  if (!canOpen) {
    return { opened: false, error: 'Cannot open PayPal on this device.' };
  }
  await Linking.openURL(url);
  return { opened: true };
}
